import type { Data } from "plotly.js";
import type { ImbalancePriceInsight } from "../../../services/client";
import { ONE_MINUTE } from "../../../util/time";

export const ACTUAL_COLOR = "#0958d9";
export const PREDICTION_COLOR = "#d46b08";
export const SURFACE_COLOR = "#ffffff";
export const GRID_COLOR = "#f0f0f0";
export const NOW_COLOR = "#595959";
export const LINE_HALO_WIDTH = 5;
export const QUARTER_HOUR = 15 * ONE_MINUTE;

export const quarterHourStarts = (
  insights: ImbalancePriceInsight[],
): number[] => {
  const starts = insights.map((insight) =>
    new Date(insight.timestamp).getTime(),
  );
  return starts.length === 0
    ? starts
    : [...starts, starts[starts.length - 1] + QUARTER_HOUR];
};

// A price holds until the next quarter hour starts, so every step followed by
// a missing value - the running quarter hour, a gap, the end of the range -
// needs its own closing point or its last quarter hour is never drawn.
const closeEachStep = (
  starts: number[],
  values: (number | null)[],
): { x: number[]; y: (number | null)[] } => {
  const x: number[] = [];
  const y: (number | null)[] = [];
  values.forEach((value, index) => {
    x.push(starts[index]);
    y.push(value);
    if (value !== null && (values[index + 1] ?? null) === null) {
      x.push(starts[index + 1]);
      y.push(value);
    }
  });
  return { x, y };
};

export const stepLine = (
  starts: number[],
  values: (number | null)[],
  name: string,
  color: string,
  yaxis = "y",
): Data[] => {
  const { x, y } = closeEachStep(starts, values);
  return [
    {
      x,
      y,
      yaxis,
      type: "scatter",
      mode: "lines",
      line: { shape: "hv", color: SURFACE_COLOR, width: LINE_HALO_WIDTH },
      legendgroup: name,
      showlegend: false,
      hoverinfo: "skip",
    },
    {
      x,
      y,
      yaxis,
      type: "scatter",
      mode: "lines",
      line: { shape: "hv", color, width: 2, dash: "solid" },
      legendgroup: name,
      name,
      hovertemplate: `%{y:.2f}<extra>${name}</extra>`,
    },
  ];
};
